import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  getDate,
  getMonth,
  format
} from 'date-fns';
import type { Schedule } from '@/services/api/scheduling';

/**
 * Represents a scheduled occurrence on a specific day
 */
export interface ScheduledDay {
  date: Date;
  schedules: ScheduleOccurrence[];
}

export interface ScheduleOccurrence {
  schedule: Schedule;
  time: string; // "6:00 AM" format
  hour: number;
  minute: number;
}

/**
 * Parse a cron expression into its components
 * Standard 5-field cron: minute hour dayOfMonth month dayOfWeek
 */
interface CronParts {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
}

function parseCron(cron: string): CronParts | null {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;

  return {
    minute: parts[0],
    hour: parts[1],
    dayOfMonth: parts[2],
    month: parts[3],
    dayOfWeek: parts[4]
  };
}

/**
 * Check if a cron field matches a given value
 * Handles: wildcard (*), specific value, ranges (1-5), lists (1,3,5), steps (star/6)
 */
// eslint-disable-next-line no-unused-vars
function fieldMatches(field: string, value: number, _maxValue: number): boolean {
  // Wildcard matches everything
  if (field === '*') return true;

  // Step pattern: */n or n/m
  if (field.includes('/')) {
    const [range, stepStr] = field.split('/');
    const step = parseInt(stepStr, 10);
    if (isNaN(step) || step <= 0) return false;

    if (range === '*') {
      return value % step === 0;
    }
    // Handle range/step like 0-23/2
    if (range.includes('-')) {
      const [start, end] = range.split('-').map(Number);
      if (value < start || value > end) return false;
      return (value - start) % step === 0;
    }
    const start = parseInt(range, 10);
    if (value < start) return false;
    return (value - start) % step === 0;
  }

  // Range pattern: n-m
  if (field.includes('-') && !field.includes(',')) {
    const [start, end] = field.split('-').map(Number);
    return value >= start && value <= end;
  }

  // List pattern: n,m,o
  if (field.includes(',')) {
    const values = field.split(',');
    return values.some(v => {
      // Handle range within list: 1,3-5,7
      if (v.includes('-')) {
        const [start, end] = v.split('-').map(Number);
        return value >= start && value <= end;
      }
      return parseInt(v, 10) === value;
    });
  }

  // Specific value
  return parseInt(field, 10) === value;
}

/**
 * Check if a cron expression runs on a specific day
 */
function cronRunsOnDay(cron: CronParts, date: Date): boolean {
  const dayOfWeek = getDay(date); // 0 = Sunday, 6 = Saturday
  const dayOfMonth = getDate(date);
  const month = getMonth(date) + 1; // 1-12

  // Check month
  if (!fieldMatches(cron.month, month, 12)) {
    return false;
  }

  // For day matching, cron has special behavior:
  // If both dayOfMonth and dayOfWeek are specified (not *), it's OR logic
  // If only one is specified, it must match
  const domSpecified = cron.dayOfMonth !== '*';
  const dowSpecified = cron.dayOfWeek !== '*';

  if (domSpecified && dowSpecified) {
    // OR logic: either day of month or day of week matches
    return (
      fieldMatches(cron.dayOfMonth, dayOfMonth, 31) || fieldMatches(cron.dayOfWeek, dayOfWeek, 6)
    );
  }

  if (domSpecified) {
    return fieldMatches(cron.dayOfMonth, dayOfMonth, 31);
  }

  if (dowSpecified) {
    return fieldMatches(cron.dayOfWeek, dayOfWeek, 6);
  }

  // Both are wildcards - runs every day
  return true;
}

/**
 * Get the scheduled time from a cron expression
 * Returns the hour and minute when the job runs
 */
function getScheduledTime(cron: CronParts): { hour: number; minute: number; timeString: string } {
  let hour = 0;
  let minute = 0;

  // Parse hour - if it's a step like */6, use the first occurrence (0)
  if (cron.hour !== '*') {
    if (cron.hour.includes('/')) {
      hour = 0;
    } else if (cron.hour.includes(',')) {
      hour = parseInt(cron.hour.split(',')[0], 10);
    } else if (cron.hour.includes('-')) {
      hour = parseInt(cron.hour.split('-')[0], 10);
    } else {
      hour = parseInt(cron.hour, 10);
    }
  }

  // Parse minute
  if (cron.minute !== '*') {
    if (cron.minute.includes('/')) {
      minute = 0;
    } else if (cron.minute.includes(',')) {
      minute = parseInt(cron.minute.split(',')[0], 10);
    } else if (cron.minute.includes('-')) {
      minute = parseInt(cron.minute.split('-')[0], 10);
    } else {
      minute = parseInt(cron.minute, 10);
    }
  }

  // Format time string
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  const timeString = `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;

  return { hour, minute, timeString };
}

/**
 * Get all days in a month where a cron expression will run
 */
export function getScheduleDaysForMonth(cronExpression: string, month: Date): Date[] {
  const cron = parseCron(cronExpression);
  if (!cron) return [];

  const start = startOfMonth(month);
  const end = endOfMonth(month);
  const allDays = eachDayOfInterval({ start, end });

  return allDays.filter(day => cronRunsOnDay(cron, day));
}

/**
 * Group schedules by the day they run within a given month
 * Returns a Map where keys are date strings (YYYY-MM-DD) and values are schedule occurrences
 */
export function groupSchedulesByDay(
  schedules: Schedule[],
  month: Date
): Map<string, ScheduleOccurrence[]> {
  const result = new Map<string, ScheduleOccurrence[]>();

  // Only include enabled schedules
  const enabledSchedules = schedules.filter(s => s.is_enabled);

  for (const schedule of enabledSchedules) {
    const cron = parseCron(schedule.cron_expression);
    if (!cron) continue;

    const runDays = getScheduleDaysForMonth(schedule.cron_expression, month);
    const { hour, minute, timeString } = getScheduledTime(cron);

    for (const day of runDays) {
      const dateKey = format(day, 'yyyy-MM-dd');
      const occurrence: ScheduleOccurrence = {
        schedule,
        time: timeString,
        hour,
        minute
      };

      const existing = result.get(dateKey) || [];
      existing.push(occurrence);
      // Sort by time
      existing.sort((a, b) => {
        if (a.hour !== b.hour) return a.hour - b.hour;
        return a.minute - b.minute;
      });
      result.set(dateKey, existing);
    }
  }

  return result;
}

/**
 * Get scheduled days for a date range (used for week view)
 */
export function getScheduledDaysInRange(
  schedules: Schedule[],
  startDate: Date,
  endDate: Date
): ScheduledDay[] {
  const result: ScheduledDay[] = [];
  const days = eachDayOfInterval({ start: startDate, end: endDate });

  // Only include enabled schedules
  const enabledSchedules = schedules.filter(s => s.is_enabled);

  for (const day of days) {
    const occurrences: ScheduleOccurrence[] = [];

    for (const schedule of enabledSchedules) {
      const cron = parseCron(schedule.cron_expression);
      if (!cron) continue;

      if (cronRunsOnDay(cron, day)) {
        const { hour, minute, timeString } = getScheduledTime(cron);
        occurrences.push({
          schedule,
          time: timeString,
          hour,
          minute
        });
      }
    }

    if (occurrences.length > 0) {
      // Sort by time
      occurrences.sort((a, b) => {
        if (a.hour !== b.hour) return a.hour - b.hour;
        return a.minute - b.minute;
      });
      result.push({ date: day, schedules: occurrences });
    }
  }

  return result;
}

/**
 * Get the number of schedules that run on a specific date
 */
export function getScheduleCountForDate(schedules: Schedule[], date: Date): number {
  return schedules.filter(schedule => {
    if (!schedule.is_enabled) return false;
    const cron = parseCron(schedule.cron_expression);
    if (!cron) return false;
    return cronRunsOnDay(cron, date);
  }).length;
}

/**
 * Determine the frequency type from a cron expression
 * Used for visual indicators on the calendar
 */
export function getCronFrequency(
  cronExpression: string
): 'daily' | 'weekly' | 'monthly' | 'custom' {
  const cron = parseCron(cronExpression);
  if (!cron) return 'custom';

  const { dayOfMonth, dayOfWeek } = cron;

  // Monthly: specific day of month, any day of week
  if (dayOfMonth !== '*' && dayOfWeek === '*') {
    return 'monthly';
  }

  // Weekly: any day of month, specific day(s) of week
  if (dayOfMonth === '*' && dayOfWeek !== '*') {
    return 'weekly';
  }

  // Daily: wildcards for both day fields
  if (dayOfMonth === '*' && dayOfWeek === '*') {
    return 'daily';
  }

  return 'custom';
}

/**
 * Get the scheduled time (hour and minute) from a cron expression
 * Useful for sorting schedules by their run time
 */
export function getScheduledTimeFromCron(cronExpression: string): {
  hour: number;
  minute: number;
  timeString: string;
} {
  const cron = parseCron(cronExpression);
  if (!cron) {
    return { hour: 0, minute: 0, timeString: '12:00 AM' };
  }
  return getScheduledTime(cron);
}
